import { useState, useEffect, useRef } from "react";
import IndividualProfile from "./IndividualProfileDash";
import IndividualProfileSkeleton from "./IndividualProfileSkeleton";
import { getAllPatients } from "../../API/PatientDirectoryCalls";
import { withTimeout } from "../../utils/apiTimeout";

type LazyIndividualProfileProps = {
  cachedData?: {
    data: {
      patients: any[];
    };
    timestamp: number;
  };
};

const CACHE_VALIDITY_MS = 5 * 60 * 1000; // 5 minutes

function LazyIndividualProfile({ cachedData }: LazyIndividualProfileProps) {
  const [isLoading, setIsLoading] = useState(!cachedData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [patientsList, setPatientsList] = useState<any[]>(
    cachedData?.data?.patients || []
  );

  const patientsListRef = useRef(patientsList);
  useEffect(() => { patientsListRef.current = patientsList; }, [patientsList]);

  useEffect(() => {
    const loadData = async () => {
      // Check if cache is still fresh (less than 5 minutes old)
      const isCacheFresh = cachedData && 
        (Date.now() - cachedData.timestamp) < CACHE_VALIDITY_MS;

      if (isCacheFresh) {
        // Cache is fresh, use it without refreshing
        setPatientsList(cachedData.data.patients || []);
        setIsLoading(false);
        return;
      }

      // If we have cached data (but it's stale), show it immediately
      // and refresh in the background
      if (cachedData) {
        setPatientsList(cachedData.data.patients || []);
        setIsLoading(false);
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      // Fetch fresh data
      try {
        setError(null);
        const freshPatients = await withTimeout(getAllPatients());

        // Only update if data actually changed
        if (JSON.stringify(freshPatients) !== JSON.stringify(patientsListRef.current)) {
          setPatientsList(freshPatients || []);
        }
      } catch (error) {
        console.error("Error loading Patient Profile data:", error);

        // If we have cached data, keep using it despite the error
        if (cachedData) {
          console.log("Using cached data despite refresh error");
          return;
        }

        // Only show error if we have no data at all
        setError(
          error instanceof Error && error.message.includes("timed out")
            ? "Loading took too long. Please refresh the page."
            : "Failed to load data. Please try again."
        );
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedData]);

  if (isLoading) {
    return <IndividualProfileSkeleton />;
  }

  if (error) {
    return (
      <div className="loadingContainer">
        <div className="body-text-size body-text">{error}</div>
        <button onClick={() => window.location.reload()}>Refresh Page</button>
      </div>
    );
  }

  return (
    <>
      {isRefreshing && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '10px 16px',
          background: '#2196F3',
          color: 'white',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: '500',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}>
          🔄 Refreshing data...
        </div>
      )}
      <IndividualProfile patients={patientsList} />
    </>
  );
}

export default LazyIndividualProfile;
